import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { SEO } from '../../seo/useSEO';
import { api, extractError } from '../../lib/api';
import { useToast } from '../../components/admin/Toast';
import type { TemplateSummary } from './EmailTemplatesPage';

interface TemplateDetail extends TemplateSummary {}

const inputClass =
  'w-full rounded-md border border-admin-border bg-white px-3 py-2 text-sm focus:border-admin-primary focus:outline-none';
const primaryBtn =
  'rounded-md bg-admin-primary px-4 py-2 text-sm font-medium text-white hover:bg-admin-primary-hover disabled:opacity-50';
const secondaryBtn =
  'rounded-md border border-admin-border bg-white px-3 py-2 text-sm hover:bg-admin-surface-alt';

const BLOCK_LABELS: Record<string, string> = {
  heading: 'Título',
  greeting: 'Saudação',
  intro: 'Introdução',
  intro1: 'Introdução (linha 1)',
  intro2: 'Introdução (linha 2)',
  cta_label: 'Botão (texto)',
  footer_note: 'Nota de rodapé',
  shipping_heading: 'Cabeçalho da morada',
  closing: 'Encerramento',
  closing1: 'Encerramento (linha 1)',
  closing2: 'Encerramento (linha 2)',
  tracking_label: 'Rótulo do rastreio',
  review_intro: 'Texto antes do botão de avaliação',
  review_cta_label: 'Botão de avaliação (texto)',
};

function blockLabel(key: string): string {
  return BLOCK_LABELS[key] ?? key;
}

export default function AdminEmailTemplateEditPage(): JSX.Element {
  const { key } = useParams<{ key: string }>();
  const [tpl, setTpl] = useState<TemplateDetail | null>(null);
  const [subject, setSubject] = useState('');
  const [blocks, setBlocks] = useState<Record<string, string>>({});
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const previewTimer = useRef<number | null>(null);
  const toast = useToast();

  useEffect(() => {
    if (!key) return;
    setLoading(true);
    void (async () => {
      try {
        const res = await api.get<TemplateDetail>(`/admin/email-templates/${key}`);
        setTpl(res.data);
        setSubject(res.data.override?.subject ?? res.data.defaults.subject);
        const initBlocks: Record<string, string> = {};
        for (const bk of res.data.blockKeys) {
          initBlocks[bk] =
            res.data.override?.blocks?.[bk] ?? res.data.defaults.blocks[bk] ?? '';
        }
        setBlocks(initBlocks);
      } catch (err) {
        toast.error(extractError(err, 'Falha ao carregar template'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const isCustomized = useMemo(() => {
    if (!tpl) return false;
    if (subject !== tpl.defaults.subject) return true;
    for (const bk of tpl.blockKeys) {
      if (blocks[bk] !== tpl.defaults.blocks[bk]) return true;
    }
    return false;
  }, [tpl, subject, blocks]);

  useEffect(() => {
    if (!key || !tpl) return;
    if (previewTimer.current) window.clearTimeout(previewTimer.current);
    previewTimer.current = window.setTimeout(async () => {
      try {
        const res = await api.post<{ subject: string; html: string }>(
          `/admin/email-templates/${key}/preview`,
          { subject, blocks }
        );
        setPreviewHtml(res.data.html);
        setPreviewSubject(res.data.subject);
      } catch {
        /* silent — preview failure should not block the editor */
      }
    }, 400);
    return () => {
      if (previewTimer.current) window.clearTimeout(previewTimer.current);
    };
  }, [key, tpl, subject, blocks]);

  const save = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!key) return;
    setSaving(true);
    try {
      await api.put(`/admin/email-templates/${key}`, { subject, blocks });
      toast.success('Template guardado.');
    } catch (err) {
      toast.error(extractError(err, 'Falha ao guardar template'));
    } finally {
      setSaving(false);
    }
  };

  const revert = async (): Promise<void> => {
    if (!key || !tpl) return;
    if (!window.confirm('Reverter para o texto original? Personalizações serão perdidas.'))
      return;
    setSaving(true);
    try {
      await api.delete(`/admin/email-templates/${key}`);
      setSubject(tpl.defaults.subject);
      const reset: Record<string, string> = {};
      for (const bk of tpl.blockKeys) reset[bk] = tpl.defaults.blocks[bk];
      setBlocks(reset);
      toast.success('Revertido para o original.');
    } catch (err) {
      toast.error(extractError(err, 'Falha ao reverter'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-admin-text-soft">A carregar…</p>;
  if (!tpl) return <p className="text-admin-danger">Template não encontrado.</p>;

  return (
    <>
      <SEO title={`Email — ${tpl.label}`} noindex />
      <div className="mb-2 text-sm">
        <Link to="/admin/emails" className="text-admin-primary hover:underline">
          ← Templates
        </Link>
      </div>
      <h1>{tpl.label}</h1>
      <p className="mt-1 mb-4 text-admin-text-soft">{tpl.description}</p>

      <div className="mb-6 rounded-md border border-admin-border bg-admin-surface-alt p-3 text-xs">
        <strong>Variáveis disponíveis:</strong>
        <ul className="mt-1 grid grid-cols-1 gap-1 sm:grid-cols-2">
          {tpl.variables.map((v) => (
            <li key={v.name}>
              <code className="rounded bg-white px-1">{`{{${v.name}}}`}</code> —{' '}
              <span className="text-admin-text-soft">{v.description}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <form onSubmit={save} className="flex flex-col gap-4">
          <div>
            <label className="block text-sm font-medium text-admin-text-soft">
              Assunto
            </label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              className={`${inputClass} mt-1`}
            />
          </div>
          {tpl.blockKeys.map((bk) => (
            <div key={bk}>
              <label className="block text-sm font-medium text-admin-text-soft">
                {blockLabel(bk)}
              </label>
              <textarea
                value={blocks[bk] ?? ''}
                onChange={(e) => setBlocks({ ...blocks, [bk]: e.target.value })}
                rows={Math.min(6, Math.max(2, ((blocks[bk] ?? '').length / 60) | 0))}
                className={`${inputClass} mt-1 font-mono`}
              />
            </div>
          ))}

          <div className="flex flex-wrap items-center gap-2">
            <button type="submit" disabled={saving} className={primaryBtn}>
              {saving ? 'A guardar…' : 'Guardar'}
            </button>
            {(tpl.override || isCustomized) ? (
              <button
                type="button"
                onClick={() => {
                  void revert();
                }}
                disabled={saving}
                className={secondaryBtn}
              >
                Reverter para o original
              </button>
            ) : null}
          </div>
        </form>

        <div>
          <p className="mb-2 text-sm font-medium text-admin-text-soft">Pré-visualização</p>
          <div className="overflow-hidden rounded-lg border border-admin-border bg-white">
            <div className="border-b border-admin-border bg-admin-surface-alt px-3 py-2 text-xs">
              <strong>Assunto:</strong> {previewSubject || subject}
            </div>
            <iframe
              title="Pré-visualização do email"
              srcDoc={previewHtml}
              className="h-[600px] w-full border-0"
              sandbox=""
            />
          </div>
        </div>
      </div>
    </>
  );
}
